import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  signal,
  DestroyRef,
} from "@angular/core";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import { MatDrawer } from "@angular/material/sidenav";
import { MatSelect } from "@angular/material/select";
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { LogoutService } from '@app/core/auth/logout.service';
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import {
  DatabaseCacheService,
  ContractInfo,
  Section,
  UserData,
} from "../../../data/cache/database-cache.service";
import { Title } from "@angular/platform-browser";
import {
  SectionGroupDocument,
  SectionGroupDetailsComponent,
} from "../../admin-system/section-group-details/section-group-details.component";
import { el } from "date-fns/locale";
import { MatDialog } from "@angular/material/dialog";
import dayjs from "dayjs";

import { GetSectionGroupService } from "../../../data/section-group/get-section-group.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { ThemeMode, ThemeService } from '@app/core/layout/theme.service';
import { AppHeaderComponent } from "src/app/shared/components/app-header/app-header.component";
import { SECTION_GROUP_ADMIN_NAV_I18N } from "./section-group-admin-nav.i18n";

@Component({
  selector: "section-group-admin-nav",
  templateUrl: "./section-group-admin-nav.component.html",
  styleUrls: ["./section-group-admin-nav.component.css"],
  imports: [...SHARED_IMPORTS, AppHeaderComponent],
})
export class SectionGroupAdminNavComponent implements OnInit, OnDestroy {
  // logoElement removed — logo is now rendered by AppHeaderComponent via [logoUrl] input
  @ViewChild("drawer") drawer: MatDrawer;
  @ViewChild("sectionGroupSelect") sectionGroupSelect: MatSelect;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly themeService = inject(ThemeService);
  private readonly logoutService = inject(LogoutService);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = SECTION_GROUP_ADMIN_NAV_I18N;

  // 変数
  error: any;
  searchText: any;
  responseData: any = [];
  userPlaceHolder: any = { user: { code: "--", lastName: "", firstName: "" } };
  tempSelectedSection: Section = null;
  confirmedSelectedSection: Section = null;
  sections: Section[] = [];
  year = new Date().getFullYear();

  yearOfPrevMonth = null;
  monthOfPrevMonth = null;

  sectionGroups: any[];
  administrateredSectionGroups: any[];
  sectionGroupIdInUrl;

  selectedSection: any;

  contractEnabled: boolean = false;
  selectedSectionGroup: any;
  selectedSectionGroupAssets: Record<string, any>;
  selectedSectionGroupFunctions: string[];
  // accountManagementRole: boolean = false;
  // userManagementRole: boolean = false;

  public selectedSectionGroupUserDataAttributes: UserData[];
  private functionsOrder = [
    "UserData",
    "RecordList",
    "Dialog",
    "Video",
    "GOST",
  ];

  public loginUser = { loginUserName: "", isLoggedIn: false };
  loginUserJob: string = "";
  toolbarTitle: string = "";
  selectedItem = 0;
  IsNavSystemOpen: boolean = true;
  isHandset$: Observable<boolean>;
  private isHandset = false;
  readonly isSidenavCollapsed = signal(false);
  isSectionSelectExpanded = false;
  private readonly NARROW_WIDTH_BREAKPOINT = 768;
  // Pending timer that opens the section group selector panel after the trigger
  // expands (mobile). Tracked so it can be cancelled and never re-opens the
  // panel after a selection / close.
  private sectionSelectOpenTimer: ReturnType<typeof setTimeout> | null = null;
  urlSelectedSectionId: string;
  readonly themeIcon = computed(() => {
    const mode = this.themeService.mode();
    if (mode === "system") return "brightness_auto";
    return mode === "dark" ? "dark_mode" : "light_mode";
  });
  readonly themeLabel = computed(() => {
    const mode = this.themeService.mode();
    if (mode === "light") return SECTION_GROUP_ADMIN_NAV_I18N.theme.switchToDark;
    if (mode === "dark") return SECTION_GROUP_ADMIN_NAV_I18N.theme.switchToSystem;
    return SECTION_GROUP_ADMIN_NAV_I18N.theme.switchToLight;
  });

  hasSectionManagement: boolean = false; // SectionGroup に 新規に Sectionを作成、または既存のSectionの変更を行う権限
  hasSectionGroupUpdate: boolean = false; // SectionGroupの名前や Attributesなどを変更する権限
  hasSectionGroupAdminManagement: boolean = false; // SectionGroupに管理者を追加したり削除したりする権限
  hasAllSectionGroupManagement: boolean = false;
  systemAdminRole = false;
  distributorAgentRole = false;

  private readonly layoutBreakpoint = inject(LayoutBreakpointService);

  constructor(
    private storeAuth: StoreAuthService,
    private router: Router,
    public common: DatabaseCacheService,
    private titleService: Title,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private getSectionGroupService: GetSectionGroupService,
  ) {}

  callFullscreen() {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  toggleTheme(): void {
    const modes: ThemeMode[] = ["light", "dark", "system"];
    const current = this.themeService.mode();
    const nextIndex = (modes.indexOf(current) + 1) % modes.length;
    this.themeService.setTheme(modes[nextIndex]);
  }

  private updateLoginUserJob(): void {
    const selectedSectionWithRole = this.common.getSelectedSectionWithRole();
    if (!selectedSectionWithRole) {
      this.loginUserJob = "";
      return;
    }
    const jobTitleCode = this.common.getSelectedSectionJobTitleCode();
    this.loginUserJob = this.common.getJobTitle(jobTitleCode);
  }

  async ngOnInit() {
    this.isHandset$ = this.layoutBreakpoint.isHandset$;

    // Track handset state for toggleSelf behavior
    this.isHandset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isHandset) => {
      this.isHandset = isHandset;
    });

    // Auto-collapse sidenav when entering tablet mode
    this.layoutBreakpoint.isTablet$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isTablet) => {
        if (isTablet) {
          this.isSidenavCollapsed.set(true);
        }
      });

    this.setPrevMonth();


    // if (this.common.getSelectedSectionGroup() == null){
    //   // await this.getUseritself();
    //   // this.common.forceSetSelectedSectionGroupById(this.sectionGroupIdInUrl);
    // }


    this.contractEnabled = true; //todo なんらかの権限も持っているときだけ true にする

    this.storeAuth
      .getLoginUserNameObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => {
        this.loginUser.loginUserName = name || "";
      });
    this.storeAuth
      .getIsLoggedInOservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoggedIn) => {
        this.loginUser.isLoggedIn = isLoggedIn;
      });

    this.sectionGroups = this.common.getSectionGroupDocuments();
    const adminSectionIds = new Set(this.common.getSectionGroupAdmins().map(admin => admin.sectionGroupId));
    if (this.common.hasAllSectionGroupManagement()) {
      this.administrateredSectionGroups = this.sectionGroups;
    } else {
      this.administrateredSectionGroups = this.sectionGroups.filter((sg) => adminSectionIds.has(sg.id));
    }
          
    this.updateLoginUserJob();

    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((noData) => {
      this.updateLoginUserJob();
    });

    // route.params.subscribe への Subscribe は this.common.parametersUpdated への Subscribe よりあとに行うこと
    // app-routing.module.ts で定義された子のsectionGroupIdパラメータを取得する
    this.route.firstChild.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
      this.setPrevMonth();

      this.sectionGroupIdInUrl = params.get("sectionGroupId"); // sectionGroupId in the URL pass
      if (this.sectionGroupIdInUrl != null) {
        this.loadSectionGroupData(this.sectionGroupIdInUrl);
      } else {
        console.error(
          "[ERROR] section-group-admin-nav.component.ts > parametersUpdated.subscribe() | sectionGroupId not properly specified in the url",
        );
      }
    });
  }

  loadSectionGroupData(sectionGroupId) {
    this.getSectionGroupService
      .getSectionGroup(sectionGroupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        let sectionGroup = res as any;

        this.common.forceSetSelectedSectionGroup(sectionGroup);

        this.selectedSectionGroup = this.common.getSelectedSectionGroup();
        this.selectedSectionGroupAssets =
          this.selectedSectionGroup.settings.assets;
        this.selectedSectionGroupFunctions =
          this.selectedSectionGroup.settings.functionAttributes;
        this.selectedSectionGroupFunctions.sort(
          (a, b) =>
            this.functionsOrder.indexOf(a) - this.functionsOrder.indexOf(b),
        );

        let sectionGroupId = this.selectedSectionGroup.id;
        this.hasSectionManagement =
          this.common.hasSectionManagement(sectionGroupId);
        this.hasSectionGroupUpdate =
          this.common.hasSectionGroupUpdate(sectionGroupId);
        this.hasSectionGroupAdminManagement =
          this.common.hasSectionGroupAdminManagement(sectionGroupId);
        this.hasAllSectionGroupManagement =
          this.common.hasAllSectionGroupManagement();

        this.systemAdminRole = this.common.getSystemAdmin();
        this.distributorAgentRole = this.common.hasAnyDistributorAgentRole();

        this.titleService.setTitle(
          this.selectedSectionGroup.name + " Cloud Admin",
        );

        // 現在のSectionGroupに対応する Sectionに管理権限を持っていれば その Section を SelectedSectionとする
        // そうでない場合は 管理権限を持っているSectionからひとつ選んで SelectedSection とする
        let section = this.common
          .getSectionDocuments()
          .find((s) => s.sectionGroupId == sectionGroupId);
        if (section != undefined) {
          this.selectedSection = section;
        } else {
          let sections = this.common.getSectionDocuments();
          if (sections.length > 0) {
            this.selectedSection = sections[0];
          } else {
            this.selectedSection = null;
          }
        }

        this.cdr.detectChanges();
      });
  }

  login() {
  }

  logout() {
    this.logoutService.logout();
  }

  onSelectionChange(event: any) {
  }

  admin() {
    this.selectedItem = 0;
  }

  sectionsList() {
    this.selectedItem = 0;
  }

  adminsList() {
    this.selectedItem = 1;
  }

  sectionGroupComparison = (option: any, value: any): boolean => {
    return option?.id === value?.id;
  };

  switchSectionGroup(event: any) {
    // Cancel any pending programmatic open and force the panel closed so the
    // custom mobile open path can't race Material's close and leave it open.
    this.cancelSectionSelectOpen();
    this.sectionGroupSelect?.close();
    this.isSectionSelectExpanded = false;

    const sectionGroup = event.value;
    const sectionGroupId = sectionGroup?.id;
    if (sectionGroupId === this.sectionGroupIdInUrl) {
      return; // 現在のSectionGroupが再選択されただけなのでなにもせず終了
    }

    this.router.navigate(["section-group-admin", sectionGroupId, "home"]);
  }

  /** Close panel on trigger pointerdown if already open (before mat-select processes click) */
  onTriggerPointerDown(event: PointerEvent): void {
    if (this.sectionGroupSelect?.panelOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.sectionGroupSelect.close();
    }
  }

  /**
   * Expand trigger on mousedown (before click opens the panel)
   * For narrow widths, prevent default and open panel after delay
   * This ensures panel position is calculated after trigger is fully expanded
   */
  onSectionSelectMousedown(event: Event): void {
    if (window.innerWidth <= this.NARROW_WIDTH_BREAKPOINT) {
      // Prevent default mat-select click behavior
      event.preventDefault();
      event.stopPropagation();

      // Expand trigger first
      this.isSectionSelectExpanded = true;

      // Wait for DOM to update, then open panel. Track the timer so a pending
      // open can be cancelled (e.g. on selection) and never stacks.
      this.cancelSectionSelectOpen();
      this.sectionSelectOpenTimer = setTimeout(() => {
        this.sectionSelectOpenTimer = null;
        this.sectionGroupSelect?.open();
      }, 50);
    }
  }

  /** Cancel any pending programmatic open of the section group selector panel */
  private cancelSectionSelectOpen(): void {
    if (this.sectionSelectOpenTimer) {
      clearTimeout(this.sectionSelectOpenTimer);
      this.sectionSelectOpenTimer = null;
    }
  }

  /**
   * Collapse trigger when panel closes
   */
  onSectionSelectClosed(): void {
    this.cancelSectionSelectOpen();
    this.isSectionSelectExpanded = false;
  }

  ngOnDestroy(): void {
    this.cancelSectionSelectOpen();
  }

  toggleSelf() {
    if (this.isHandset) {
      // Mobile: toggle drawer (overlay mode only)
      this.drawer?.toggle();
    } else {
      // Tablet/Desktop: toggle collapsed state
      this.isSidenavCollapsed.update(v => !v);
    }
  }

  openNewWindow(ruterurl) {
    // Converts the route into a string that can be used
    // with the window.open() function
    const url = this.router.serializeUrl(this.router.createUrlTree([ruterurl]));
    window.open("#" + url, "_blank");
  }

  openSectionGroupDetailDialog() {
    // let sectionGroupData: SectionGroupData = {
    //   id: this.selectedSectionGroup.id,
    //   name: this.selectedSectionGroup.name,

    //   zip: this.selectedSectionGroup.addresses.zip,
    //   prefecture: this.selectedSectionGroup.addresses.prefecture,
    //   city: this.selectedSectionGroup.addresses.city,
    //   town: this.selectedSectionGroup.addresses.town,
    //   line1: this.selectedSectionGroup.addresses.line1,
    //   line2: this.selectedSectionGroup.addresses.line2,

    //   phone: this.selectedSectionGroup.contactDetails.phone,
    //   mobile: this.selectedSectionGroup.contactDetails.mobile,
    //   email: this.selectedSectionGroup.contactDetails.email,

    //   lastModifiedAt: dayjs(
    //     this.selectedSectionGroup.contactDetails.contractEndAt,
    //   ).isValid()
    //     ? dayjs(this.selectedSectionGroup.contactDetails.contractEndAt).format(
    //         "YYYY/MM/DD",
    //       )
    //     : null,
    //   lastModifiedBy: dayjs(
    //     this.selectedSectionGroup.contactDetails.contractEndAt,
    //   ).isValid()
    //     ? dayjs(this.selectedSectionGroup.contactDetails.contractEndAt).format(
    //         "YYYY/MM/DD",
    //       )
    //     : null,

    //   logoUrl: "",

    //   monthlyQuotaGB: this.selectedSectionGroup.settings.monthlyQuotaGB,

    //   userDataAttributes: JSON.stringify(
    //     this.selectedSectionGroup.settings.userDataAttributes,
    //   ),
    //   functionAttributes: this.selectedSectionGroup.settings.functionAttributes,

    //   admins: null,
    // };
    let sectionGroupData = this.selectedSectionGroup;

    const dialogRef = this.dialog.open(SectionGroupDetailsComponent, {
      width: "740px",
      disableClose: false,
      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        sectionGroupData: sectionGroupData,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (result) => {
      if (result?.needRefresh) {
        await this.common.refreshAllData();
        this.loadSectionGroupData(this.sectionGroupIdInUrl);
      }
    });
  }

  setPrevMonth() {
    this.yearOfPrevMonth = new Date().getFullYear();
    this.monthOfPrevMonth = new Date().getMonth();

    if (this.monthOfPrevMonth == 0) {
      this.monthOfPrevMonth = 12;
      this.yearOfPrevMonth--;
    }
  }

  async getUseritself(): Promise<void> {
    try {
      this.urlSelectedSectionId = this.route.snapshot.paramMap.get("sectionId"); // sectionId in the URL pass
      const responseData = await this.common.setLoginUserAndSelectedSection(
        this.urlSelectedSectionId,
      );
    } catch (error) {
    }
  }
}
